import { t } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import { cn } from '@repo/ui/lib/utils';
import { useEffect, useRef, useState } from 'react';

interface DocumentScannerProps {
  isScanning: boolean;
  progress?: number;
  className?: string;
  /** When true, scanner overlay is always visible for testing. */
}

const SCAN_DURATION = 2.8;

/** Vertical positions (0–100) for simulated "text lines" that light up as the scan passes */
const TEXT_LINE_POSITIONS = [10, 20, 32, 42, 52, 62, 74, 84];

/** Document bounds relative to container: overlay is positioned only over the actual document area */
interface DocumentRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

function measureDocumentRect(
  container: HTMLDivElement,
  image: HTMLDivElement,
): DocumentRect | null {
  if (!image) return null;
  const containerRect = container.getBoundingClientRect();
  const imageRect = image.getBoundingClientRect();
  return {
    top: containerRect.y,
    left: containerRect.x,
    width: imageRect.width,
    height: imageRect.height,
  };
}

export const DocumentScanner = ({
  isScanning,
  progress = 0,
  className,
  // containerRef,
}: DocumentScannerProps) => {
  useLocale();

  const containerRef = useRef<HTMLDivElement | null>(null);

  const [documentRect, setDocumentRect] = useState<DocumentRect | null>(null);

  useEffect(() => {
    const container = containerRef?.current;
    if (!container || !container) return;

    const updateRect = () => {
      const rect = measureDocumentRect(container, container);
      setDocumentRect(rect);
    };

    updateRect();

    const resizeObserver = new ResizeObserver(updateRect);
    resizeObserver.observe(container);

    if ('complete' in container && container.complete) {
      updateRect();
    } else if (container instanceof HTMLDivElement) {
      container.addEventListener('load', updateRect);
    }

    const interval = window.setInterval(updateRect, 500);
    return () => {
      resizeObserver.disconnect();
      if (container instanceof HTMLDivElement) {
        container.removeEventListener('load', updateRect);
      }
      window.clearInterval(interval);
    };
  }, [containerRef, isScanning]);

  if (!isScanning) return null;

  const useDocumentArea = documentRect && documentRect.width > 0 && documentRect.height > 0;
  const overlayStyle: React.CSSProperties = useDocumentArea
    ? {
        position: 'fixed',
        // top: documentRect.top,
        // left: documentRect.left,
        width: documentRect.width,
        height: documentRect.height,
      }
    : { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 };

  return (
    <div
      className={cn(
        'pointer-events-none absolute inset-0 z-100 overflow-hidden rounded-lg',
        className,
      )}
      ref={containerRef}
    >
      {/* Overlay only over the document area (actual image), not the full box */}
      <div
        className="overflow-hidden rounded-sm bg-muted/30"
        style={{
          ...overlayStyle,
          minWidth: useDocumentArea ? documentRect!.width : undefined,
          minHeight: useDocumentArea ? documentRect!.height : undefined,
          boxShadow: 'inset 0 0 80px 20px rgba(0,0,0,0.06)',
        }}
      >
        {/* Simulated text lines – each line "lights up" as the scan passes over it */}
        {TEXT_LINE_POSITIONS.map((topPct) => (
          <div
            className="pointer-events-none absolute start-3 end-3 rounded"
            key={topPct}
            style={{
              top: `${topPct}%`,
              height: '5%',
              minHeight: 16,
              backgroundColor: 'color-mix(in srgb, var(--scanner-line) 14%, transparent)',
              animation: `scanner-text-line-read ${SCAN_DURATION}s linear infinite`,
              animationDelay: `${(topPct / 100) * SCAN_DURATION}s`,
              animationFillMode: 'both',
            }}
          />
        ))}

        {/* Wide reading band – area that looks like it's being "read" (softer) */}
        <div
          className="pointer-events-none absolute start-0 end-0 w-full"
          style={{
            height: '32%',
            top: '-20%',
            background:
              'linear-gradient(to bottom, transparent 0%, color-mix(in srgb, var(--scanner-line) 10%, transparent) 25%, color-mix(in srgb, var(--scanner-line) 16%, transparent) 50%, color-mix(in srgb, var(--scanner-line) 10%, transparent) 75%, transparent 100%)',
            animation: `scanner-sweep-band ${SCAN_DURATION}s ease-in-out infinite`,
          }}
        />

        {/* Main laser scan line – reduced glow intensity */}
        <div
          className="scanner-sweep-line absolute start-0 end-0 top-0 h-[5px]"
          style={{ willChange: 'transform' }}
        >
          <div
            className="absolute inset-0 -my-6 opacity-40"
            style={{
              background:
                'linear-gradient(to right, transparent 0%, var(--scanner-line) 50%, transparent 100%)',
              filter: 'blur(16px)',
            }}
          />
          <div
            className="absolute inset-0 -my-3 opacity-55"
            style={{
              background:
                'linear-gradient(to right, transparent 5%, var(--scanner-glow) 50%, transparent 95%)',
              filter: 'blur(8px)',
            }}
          />
          <div
            className="absolute inset-0 opacity-85"
            style={{
              background: 'var(--scanner-line)',
              boxShadow: '0 0 12px 2px color-mix(in srgb, var(--scanner-glow) 50%, transparent)',
            }}
          />
        </div>

        {/* "Reading" status when scanning */}
        {isScanning && (
          <div className="absolute top-3 left-1/2 flex -translate-x-1/2 items-center gap-2 rounded-full border border-primary/30 bg-background/90 px-3 py-1.5 text-primary/90 shadow-lg backdrop-blur-sm">
            <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-primary" />
            <span className="font-medium text-xs">{t('reports.readingDocument')}</span>
          </div>
        )}

        {/* Progress bar when actually scanning */}
        {isScanning && (
          <div className="absolute start-3 end-3 bottom-3 h-2 overflow-hidden rounded-full bg-black/15 dark:bg-white/15">
            <div
              className="h-full rounded-full bg-primary transition-all duration-300 ease-out"
              style={{ width: `${Math.min(100, progress)}%` }}
            />
          </div>
        )}
      </div>
    </div>
  );
};
